'use client';

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

export interface ShareOptions {
  title?: string;
  text?: string;
  url: string;
}

// The native plugin rejects with a "canceled"/"dismissed" message when the
// user closes the share sheet without choosing an app — that's a success path,
// not a reason to fall back to copying the link.
function isCancellation(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '').toLowerCase();
  return msg.includes('cancel') || msg.includes('dismiss') || msg.includes('abort');
}

/**
 * Opens the native share sheet (WhatsApp, Facebook, Messages, etc.).
 *
 * - Native (Capacitor): uses the @capacitor/share plugin → the OS share sheet.
 *   Statically imported so it's in the main bundle (a dynamic import() would be
 *   a separate chunk that can fail to load from a remote server.url). We try
 *   the plugin directly rather than gating on isPluginAvailable(), which can
 *   under-report when the web bundle is served from a remote URL.
 * - Web: falls back to the Web Share API, then to copying the link. NOTE: the
 *   Android WebView does NOT implement navigator.share, so the native plugin
 *   above is required there — without it the only option is the clipboard.
 *
 * Returns true if a share/copy path was triggered, false otherwise.
 */
export async function nativeShare(options: ShareOptions): Promise<boolean> {
  const { title, text, url } = options;

  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({ title, text, url, dialogTitle: 'Share' });
      return true;
    } catch (err) {
      // User dismissed the sheet → done, don't silently copy the link.
      if (isCancellation(err)) return false;
      // Otherwise the plugin isn't available in this build — fall through.
    }
  }

  // Web Share API (opens the OS share sheet on supporting browsers).
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share({ title, text, url });
      return true;
    }
  } catch (err) {
    if (isCancellation(err)) return false;
    // Unsupported → fall through to clipboard.
  }

  // Last resort: copy the link.
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // Ignore — nothing more we can do.
  }

  return false;
}
